import { Ap, b, DOM, E, flow, IO, IOO, O, pipe, RA, RNEA, s, t } from '@werk85/prelude'
import { HashNavigation, Navigation, Pagination, Swiper } from 'swiper'

import 'bootstrap'
import '../css/main.scss'

pipe(
  IOO.of({ window }),
  IOO.chainFirst(({ window: { document } }) =>
    pipe(
      document,
      DOM.querySelector('.swiper'),
      IOO.map(swiper => initSwiper(swiper as HTMLElement)),
      IOO.chainFirstIOK(swiper =>
        // services page: on uncollapsing a service, fade out other services
        // set height of uncollapsed container to content height
        pipe(
          document,
          DOM.querySelectorAll('.page-services .col-service'),
          // create a tuple of service and wrapper
          IOO.map(
            RA.filterMap(service =>
              Ap.sequenceT(IOO.Applicative)(
                IOO.of(service),
                pipe(service, DOM.querySelector('.service-wrapper')) as IOO.IOOption<HTMLElement>
              )()
            )
          ),
          IOO.map(services =>
            pipe(
              services,
              RA.map(([service, wrapper]) => {
                pipe(
                  service,
                  DOM.querySelector('.collapse'),
                  IOO.chainFirstIOK(
                    DOM.addEventListener_('show.bs.collapse' as any)(
                      flow(
                        IOO.fromPredicate(({ target, currentTarget }) => target === currentTarget),
                        IOO.map(() => {
                          pipe(
                            services,
                            RA.reject(([s, _]) => s === service),
                            RA.map(([service, _]) => service.classList.add('hide'))
                          )
                        })
                      )
                    )
                  ),
                  IOO.chainFirstIOK(
                    DOM.addEventListener_('transitionend')(() => () => {
                      // set height of uncollapsed content wrapper to content height
                      pipe(
                        service,
                        DOM.querySelector('.collapse'),
                        IOO.fold(
                          () => () => 'auto',
                          collapse => () => `${collapse.scrollHeight}px`
                        ),
                        IO.map(height => (wrapper.style.minHeight = height)),
                        IO.apFirst(() => window.setTimeout(() => swiper.updateAutoHeight(0), 650)) // setTimeout is needed to wait for css transition to finish
                      )()
                    })
                  ),
                  IOO.chainFirstIOK(
                    DOM.addEventListener_('hide.bs.collapse' as any)(
                      flow(
                        IOO.fromPredicate(({ target, currentTarget }) => target === currentTarget),
                        IOO.map(e => {
                          console.log('hide.bs.collapse', e.target === e.currentTarget)
                          pipe(
                            services,
                            RA.reject(([s, _]) => s === service),
                            RA.map(([service, _]) => service.classList.remove('hide'))
                          )

                          // reset height of to-collapse content wrapper to content height
                          wrapper.style.minHeight = 'auto'
                          window.setTimeout(() => swiper.updateAutoHeight(0))
                        })
                      )
                    )
                  )
                )()
              })
            )
          )
        )
      )
    )
  )
)()

pipe(
  IOO.of({ window }),
  IOO.bind('hash', ({ window }) => IOO.of(window.location.hash)),
  IOO.bind('homeIndicator', ({ window: { document } }) => pipe(document, DOM.querySelector('.swiper-pagination-bullet#moinsen'))),
  IOO.bind(
    'swiperPagination',
    ({ window: { document } }) =>
      pipe(document, DOM.querySelectorAll('.swiper-pagination-bullet')) as unknown as IOO.IOOption<
        RNEA.ReadonlyNonEmptyArray<HTMLElement>
      >
  ),
  IOO.bind('swiperWrapper', ({ window: { document } }) => pipe(document, DOM.querySelector('.swiper-wrapper'))),
  IOO.map(({ homeIndicator, hash, swiperPagination, swiperWrapper }) => {
    pipe(
      hash,
      t.NonEmptyString.decode,
      E.fold(
        () => homeIndicator.classList.add('swiper-pagination-bullet-active'),
        hash =>
          pipe(
            swiperPagination,
            RA.map(indicator =>
              pipe(
                indicator.getAttribute('id'),
                O.fromNullable,
                O.map(id => s.Semigroup.concat('#', id)),
                O.map(id => s.Eq.equals(id, hash)),
                O.map(
                  b.fold(
                    () => {
                      indicator.classList.remove('swiper-pagination-bullet-active')
                    },
                    () => {
                      swiperWrapper.classList.add('no-transition')
                      indicator.classList.add('swiper-pagination-bullet-active')
                      indicator.click()
                      window.scrollTo(0, 0)
                      swiperWrapper.classList.remove('no-transition')
                    }
                  )
                )
              )
            )
          )
      )
    )
  })
)()

const buildUrl = (hash: string) => `${window.location.protocol}//${window.location.hostname}/#${hash}`

pipe(
  IOO.of({ document }),
  IOO.bind('carouselIndicators', ({ document }) => pipe(document, DOM.querySelectorAll('.swiper-pagination > button'))),
  IOO.bind('carouselMenu', ({ document }) => pipe(document, DOM.querySelector('#collapseIndicators'))),
  IOO.bind('navItems', ({ document }) => pipe(document, DOM.querySelectorAll('.nav-item'))),
  IOO.bind('toggleMenu', ({ document }) => pipe(document, DOM.querySelector('.btn-toggle-indicators'))),
  IOO.map(({ carouselIndicators, carouselMenu, navItems, toggleMenu }) => {
    toggleMenu.addEventListener('click', () => {
      carouselMenu.classList.add('collapse')
      !toggleMenu.classList.contains('collapsed') && carouselMenu.classList.toggle('show')
      // carouselMenu.classList.remove('collapsing')
      carouselMenu.removeAttribute('style')
    })
    carouselIndicators.forEach(item => {
      item.addEventListener('click', () => {
        carouselMenu.classList.remove('show')
        toggleMenu.classList.add('collapsed')
        toggleMenu.setAttribute('aria-expanded', 'false')
        window.location.href = buildUrl(item.id)
        window.scrollTo(0, 0)
      })
    })
    navItems.forEach(item => {
      item.addEventListener('click', () => {
        toggleMenu.classList.add('no-transition')
        carouselMenu.classList.add('no-transition')
        carouselMenu.classList.remove('show')
        toggleMenu.classList.add('collapsed')
        toggleMenu.setAttribute('aria-expanded', 'false')
        setTimeout(() => {
          toggleMenu.classList.remove('no-transition')
          carouselMenu.classList.remove('no-transition')
        }, 100)
      })
    })
  })
)()

const removeCollapsingClass = (item: Element) => {
  const id = item.getAttribute('aria-controls')

  pipe(
    id,
    O.fromNullable,
    O.fold(
      () => null,
      id => {
        item.addEventListener('click', () => {
          pipe(
            IOO.Do,
            IOO.apS('container', pipe(document, DOM.querySelector(`#${id}`)) as IOO.IOOption<HTMLElement>),
            IOO.map(({ container }) => {
              container.classList.add('collapse')
              !item.classList.contains('collapsed') && container.classList.toggle('show')
              container.classList.remove('collapsing')
              container.style.height = ''
            })
          )()
        })
      }
    )
  )
}

pipe(
  IOO.of({ document }),
  IOO.bind('stretchedLinks', ({ document }) => pipe(document, DOM.querySelectorAll('.content-open'))),
  IOO.bind('closeButtons', ({ document }) => pipe(document, DOM.querySelectorAll('.content-close'))),
  IOO.bind('backdrops', ({ document }) => pipe(document, DOM.querySelectorAll('.content-backdrop'))),
  IOO.map(({ stretchedLinks, closeButtons, backdrops }) => {
    stretchedLinks.forEach(link => {
      removeCollapsingClass(link)
    })
    closeButtons.forEach(button => {
      removeCollapsingClass(button)
    })
    backdrops.forEach(backdrop => {
      removeCollapsingClass(backdrop)
    })
  })
)()

function initSwiper(swiper: HTMLElement): Swiper {
  const bulletOptions = [
    {
      id: 'moinsen',
      title: 'Moinsen'
    },
    {
      id: 'ueber-sandra',
      title: 'Über Sandra'
    },
    {
      id: 'services',
      title: 'Leistungen'
    },
    {
      id: 'newsroom',
      title: 'Newsroom'
    },
    {
      id: 'kontakt',
      title: 'Kontakt'
    }
  ]

  return new Swiper(swiper, {
    direction: 'horizontal',
    loop: true,
    speed: 750,
    modules: [HashNavigation, Navigation, Pagination],
    hashNavigation: {
      watchState: true
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      type: 'bullets',
      renderBullet: (index, className) => {
        return pipe(
          bulletOptions,
          RA.lookup(index),
          O.foldMap(s.Monoid)(
            option =>
              `<button type="button" data-bs-target="#carouselPages" class="${className}" id="${option.id}"><span>${option.title}</span></button>`
          )
        )
      }
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    },
    autoHeight: true,
    shortSwipes: false,
    longSwipesRatio: 0.1,
    longSwipesMs: 100,
    on: {
      slideChange: () => {
        pipe(
          document,
          DOM.querySelector('.swiper-pagination-bullet-active'),
          IOO.map(activeIndicator =>
            pipe(
              activeIndicator.getAttribute('id'),
              O.fromNullable,
              O.map(id => (window.location.hash = `#${id}`))
            )
          )
        )()
      },
      slideChangeTransitionStart: () => {
        // scroll to top
        window.scrollTo(0, 0)
      }
    }
  })
}
